import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, DirectionsRenderer, TrafficLayer } from '@react-google-maps/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Truck, Building2, Navigation, RefreshCw, Layers, Clock, Route, AlertTriangle } from 'lucide-react';
import api from '../services/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Map container style
const containerStyle = {
  width: '100%',
  height: '550px'
};

// UK center
const defaultCenter = {
  lat: 53.0,
  lng: -2.0
};

// Custom marker icons
const createTruckIcon = (color = '#f97316') => ({
  url: 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="28" height="28">
      <rect x="2" y="8" width="28" height="16" rx="3" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      <rect x="4" y="10" width="10" height="8" rx="1" fill="#fff" opacity="0.9"/>
      <circle cx="8" cy="22" r="3" fill="#333" stroke="#fff" stroke-width="1"/>
      <circle cx="24" cy="22" r="3" fill="#333" stroke="#fff" stroke-width="1"/>
      <rect x="16" y="12" width="12" height="4" rx="1" fill="#fff" opacity="0.3"/>
    </svg>
  `),
  scaledSize: { width: 28, height: 28 },
  anchor: { x: 14, y: 14 }
});

const markerIcons = {
  depot: {
    url: 'data:image/svg+xml,' + encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 44" width="36" height="44">
        <path d="M18 0 C8 0 0 8 0 18 C0 32 18 44 18 44 C18 44 36 32 36 18 C36 8 28 0 18 0 Z" fill="#0f172a" stroke="#fff" stroke-width="2"/>
        <rect x="8" y="10" width="20" height="14" rx="2" fill="#fff"/>
        <rect x="10" y="12" width="6" height="5" fill="#0f172a"/>
        <rect x="20" y="12" width="6" height="5" fill="#0f172a"/>
        <rect x="14" y="19" width="8" height="5" fill="#0f172a"/>
      </svg>
    `),
    scaledSize: { width: 36, height: 44 },
    anchor: { x: 18, y: 44 }
  },
  site: {
    url: 'data:image/svg+xml,' + encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 36" width="28" height="36">
        <path d="M14 0 C6 0 0 6 0 14 C0 25 14 36 14 36 C14 36 28 25 28 14 C28 6 22 0 14 0 Z" fill="#3b82f6" stroke="#fff" stroke-width="2"/>
        <circle cx="14" cy="12" r="5" fill="#fff"/>
      </svg>
    `),
    scaledSize: { width: 28, height: 36 },
    anchor: { x: 14, y: 36 }
  }
};

// Route colors
const routeColors = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f59e0b', '#ef4444'];

// Format duration in hours and minutes
const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Format distance in km or miles
const formatDistance = (meters) => {
  const miles = meters / 1609.34;
  return `${miles.toFixed(1)} mi`;
};

// Calculate ETA from current time
const calculateETA = (durationSeconds) => {
  const now = new Date();
  const eta = new Date(now.getTime() + durationSeconds * 1000);
  return eta.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const MapsPage = () => {
  const [activeView, setActiveView] = useState('depots');
  const [depots, setDepots] = useState([]);
  const [sites, setSites] = useState([]);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [map, setMap] = useState(null);
  const [directionsResults, setDirectionsResults] = useState([]);
  const [calculatingRoutes, setCalculatingRoutes] = useState(false);
  const [showTraffic, setShowTraffic] = useState(true); // Traffic layer toggle - on by default
  const directionsServiceRef = useRef(null);

  // Load Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || ''
  });

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    fetchMapData();
  }, []);

  // Initialize DirectionsService when map is loaded
  useEffect(() => {
    if (isLoaded && window.google) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
  }, [isLoaded]);

  // Calculate directions when switching to routes view
  useEffect(() => {
    if (isLoaded && map && routes.length > 0 && (activeView === 'routes' || activeView === 'all') && directionsServiceRef.current) {
      calculateAllRouteDirections();
    }
  }, [isLoaded, map, routes, activeView]);

  const calculateAllRouteDirections = async () => {
    if (!directionsServiceRef.current || calculatingRoutes) return;
    
    setCalculatingRoutes(true);
    const results = [];
    
    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];
      if (!route.stops || route.stops.length < 2) continue;
      
      try {
        const origin = { lat: route.stops[0].lat, lng: route.stops[0].lng };
        const destination = { lat: route.stops[route.stops.length - 1].lat, lng: route.stops[route.stops.length - 1].lng };
        
        // Create waypoints from intermediate stops
        const waypoints = route.stops.slice(1, -1).map(stop => ({
          location: new window.google.maps.LatLng(stop.lat, stop.lng),
          stopover: true
        }));
        
        const result = await new Promise((resolve, reject) => {
          directionsServiceRef.current.route(
            {
              origin: new window.google.maps.LatLng(origin.lat, origin.lng),
              destination: new window.google.maps.LatLng(destination.lat, destination.lng),
              waypoints: waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
              optimizeWaypoints: false,
              // Truck-friendly routing options
              drivingOptions: {
                departureTime: new Date(), // Use current time for live traffic
                trafficModel: window.google.maps.TrafficModel.PESSIMISTIC // Avoid traffic by using worst-case estimates
              },
              avoidHighways: false, // Trucks prefer highways
              avoidTolls: false, // Allow toll roads for efficiency
              avoidFerries: true // Avoid ferries for reliability
            },
            (response, status) => {
              if (status === 'OK') {
                resolve(response);
              } else {
                console.log(`Directions request failed for ${route.route_name}: ${status}`);
                reject(new Error(status));
              }
            }
          );
        });
        
        // Extract route summary info (total duration and distance)
        let totalDuration = 0;
        let totalDurationInTraffic = 0;
        let totalDistance = 0;
        const legDetails = [];
        
        if (result.routes[0] && result.routes[0].legs) {
          result.routes[0].legs.forEach((leg, legIdx) => {
            totalDuration += leg.duration.value;
            totalDurationInTraffic += leg.duration_in_traffic?.value || leg.duration.value;
            totalDistance += leg.distance.value;
            
            // Calculate ETA for each stop
            const cumulativeDuration = result.routes[0].legs
              .slice(0, legIdx + 1)
              .reduce((sum, l) => sum + (l.duration_in_traffic?.value || l.duration.value), 0);
            
            legDetails.push({
              stopNumber: legIdx + 1,
              address: route.stops[legIdx + 1]?.address || leg.end_address,
              duration: leg.duration_in_traffic?.value || leg.duration.value,
              distance: leg.distance.value,
              eta: calculateETA(cumulativeDuration)
            });
          });
        }
        
        results.push({
          routeId: route.load_id,
          routeName: route.route_name,
          directions: result,
          color: routeColors[i % routeColors.length],
          routeData: route,
          // Enhanced route info
          totalDuration,
          totalDurationInTraffic,
          totalDistance,
          legDetails,
          // Traffic delay detection: >5% delay indicates disruption
          hasTrafficDelay: totalDurationInTraffic > totalDuration * 1.05,
          // Calculate delay in minutes for display
          delayMinutes: Math.round((totalDurationInTraffic - totalDuration) / 60)
        });
        
        // Small delay between requests to avoid rate limiting
        await new Promise(r => setTimeout(r, 300));
        
      } catch (error) {
        console.log(`Failed to calculate route for ${route.route_name}:`, error.message);
      }
    }
    
    setDirectionsResults(results);
    setCalculatingRoutes(false);
  };

  const fetchMapData = async () => {
    setLoading(true);
    setDirectionsResults([]); // Clear previous directions
    try {
      const [depotsRes, sitesRes, deliveriesRes, routesRes, driversRes] = await Promise.all([
        api.get('/portal/map/depots').catch(() => ({ data: [] })),
        api.get('/portal/map/my-sites').catch(() => ({ data: [] })),
        api.get('/portal/map/active-deliveries').catch(() => ({ data: [] })),
        api.get('/portal/map/routes/today').catch(() => ({ data: [] })),
        api.get('/driver/locations/all').catch(() => ({ data: { drivers: [] } }))
      ]);
      
      setDepots(depotsRes.data || []);
      setSites(sitesRes.data || []);
      setActiveDeliveries(deliveriesRes.data || []);
      setRoutes(routesRes.data || []);
      setDriverLocations(driversRes.data?.drivers || []);
    } catch (error) {
      console.error('Error fetching map data:', error);
    }
    setLoading(false);
  };
  
  // Auto-refresh driver locations every 10 seconds when on drivers tab
  useEffect(() => {
    if (activeView !== 'drivers') return;
    
    const fetchDriverLocations = async () => {
      try {
        const res = await api.get('/driver/locations/all');
        setDriverLocations(res.data?.drivers || []);
      } catch (error) {
        console.error('Error fetching driver locations:', error);
      }
    };
    
    const interval = setInterval(fetchDriverLocations, 10000);
    return () => clearInterval(interval);
  }, [activeView]);

  const getDepotMarkers = () => {
    if (activeView !== 'depots' && activeView !== 'all') return [];
    return depots.filter(d => d.coordinates).map(depot => ({
      type: 'depot',
      name: depot.name,
      address: `${depot.city}, ${depot.postcode}`,
      position: { lat: depot.coordinates.lat, lng: depot.coordinates.lng },
      data: depot
    }));
  };

  const getSiteMarkers = () => {
    if (activeView !== 'sites' && activeView !== 'all') return [];
    return sites.filter(s => s.coordinates).map(site => ({
      type: 'site',
      name: site.name,
      address: `${site.city}, ${site.postcode}`,
      position: { lat: site.coordinates.lat, lng: site.coordinates.lng },
      data: site
    }));
  };

  const getVehicleMarkers = () => {
    if (activeView !== 'vehicles' && activeView !== 'all') return [];
    return activeDeliveries.filter(d => d.vehicle_position).map(delivery => ({
      type: 'vehicle',
      registration: delivery.vehicle_registration || 'XX00 XXX',
      name: delivery.driver_name || 'Driver',
      status: delivery.status,
      position: { lat: delivery.vehicle_position.lat, lng: delivery.vehicle_position.lng },
      data: delivery
    }));
  };

  const getDriverMarkers = () => {
    if (activeView !== 'drivers' && activeView !== 'all') return [];
    return driverLocations.map(driver => ({
      type: 'driver',
      name: driver.driver_name || 'Driver',
      registration: driver.vehicle_reg || 'No vehicle',
      position: { lat: driver.latitude, lng: driver.longitude },
      accuracy: driver.accuracy,
      speed: driver.speed,
      heading: driver.heading,
      updated_at: driver.updated_at,
      data: driver
    }));
  };

  // Get directions to display
  const getDirectionsToDisplay = () => {
    if (activeView !== 'routes' && activeView !== 'all') return [];
    
    if (selectedRoute) {
      return directionsResults.filter(d => d.routeId === selectedRoute);
    }
    return directionsResults;
  };

  // Handle route selection
  const handleRouteClick = (routeId) => {
    if (selectedRoute === routeId) {
      setSelectedRoute(null);
    } else {
      setSelectedRoute(routeId);
      
      // Zoom to selected route
      const routeResult = directionsResults.find(d => d.routeId === routeId);
      if (routeResult && routeResult.directions && map) {
        const bounds = routeResult.directions.routes[0].bounds;
        map.fitBounds(bounds, { padding: 50 });
      }
    }
  };

  // Fit bounds to markers
  useEffect(() => {
    if (map && !loading && isLoaded && !selectedRoute) {
      const allMarkers = [
        ...getDepotMarkers(),
        ...getSiteMarkers(),
        ...getVehicleMarkers(),
        ...getDriverMarkers()
      ];
      
      if (allMarkers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        allMarkers.forEach(marker => {
          bounds.extend(marker.position);
        });
        
        // Also include route bounds if in routes view
        if ((activeView === 'routes' || activeView === 'all') && directionsResults.length > 0) {
          directionsResults.forEach(dr => {
            if (dr.directions && dr.directions.routes[0]) {
              bounds.union(dr.directions.routes[0].bounds);
            }
          });
        }
        
        map.fitBounds(bounds, { padding: 50 });
      }
    }
  }, [map, activeView, loading, depots, sites, activeDeliveries, driverLocations, isLoaded, directionsResults]);

  if (loading && !isLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Error loading Google Maps</p>
        <p className="text-sm text-muted-foreground mt-2">{loadError.message}</p>
      </div>
    );
  }

  const depotMarkers = getDepotMarkers();
  const siteMarkers = getSiteMarkers();
  const vehicleMarkers = getVehicleMarkers();
  const driverMarkers = getDriverMarkers();
  const directionsToDisplay = getDirectionsToDisplay();

  return (
    <div className="space-y-6" data-testid="maps-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Maps & Tracking</h1>
          <p className="text-muted-foreground">View depot locations, delivery routes, and vehicle positions</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => {
            setSelectedRoute(null);
            setDirectionsResults([]);
            fetchMapData();
          }}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={`hover:border-orange-500/50 transition-colors cursor-pointer ${activeView === 'depots' ? 'border-orange-500' : ''}`} onClick={() => { setActiveView('depots'); setSelectedRoute(null); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Depots</p>
                <p className="text-2xl font-bold">{depots.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-navy-900/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-navy-900 dark:text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`hover:border-orange-500/50 transition-colors cursor-pointer ${activeView === 'sites' ? 'border-orange-500' : ''}`} onClick={() => { setActiveView('sites'); setSelectedRoute(null); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sites</p>
                <p className="text-2xl font-bold">{sites.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`hover:border-orange-500/50 transition-colors cursor-pointer ${activeView === 'vehicles' ? 'border-orange-500' : ''}`} onClick={() => { setActiveView('vehicles'); setSelectedRoute(null); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vehicles</p>
                <p className="text-2xl font-bold">{activeDeliveries.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Truck className="w-5 h-5 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`hover:border-orange-500/50 transition-colors cursor-pointer ${activeView === 'drivers' ? 'border-orange-500' : ''}`} onClick={() => { setActiveView('drivers'); setSelectedRoute(null); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Drivers</p>
                <p className="text-2xl font-bold text-green-600">{driverLocations.length}</p>
                <p className="text-xs text-green-500">Live</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Navigation className="w-5 h-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`hover:border-orange-500/50 transition-colors cursor-pointer ${activeView === 'routes' ? 'border-orange-500' : ''}`} onClick={() => { setActiveView('routes'); setSelectedRoute(null); }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Routes</p>
                <p className="text-2xl font-bold">{routes.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Navigation className="w-5 h-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Google Maps
                {calculatingRoutes && (
                  <span className="text-sm font-normal text-orange-500 ml-2 flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full" />
                    Calculating routes...
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {activeView === 'depots' && `Showing ${depots.length} depot locations`}
                {activeView === 'sites' && `Showing ${sites.length} customer delivery sites`}
                {activeView === 'vehicles' && `Showing ${activeDeliveries.length} active vehicles (click for registration)`}
                {activeView === 'drivers' && `Showing ${driverLocations.length} active drivers (live tracking)`}
                {activeView === 'routes' && `Showing ${directionsResults.length}/${routes.length} routes following roads`}
                {activeView === 'all' && 'Showing all locations and routes'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant={showTraffic ? "default" : "outline"}
                size="sm"
                onClick={() => setShowTraffic(!showTraffic)}
                className={`gap-1 ${showTraffic ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
              >
                <Layers className="w-4 h-4" />
                Traffic
              </Button>
              <Tabs value={activeView} onValueChange={(v) => { setActiveView(v); setSelectedRoute(null); }}>
                <TabsList>
                  <TabsTrigger value="depots" data-testid="view-depots">Depots</TabsTrigger>
                  <TabsTrigger value="sites" data-testid="view-sites">Sites</TabsTrigger>
                  <TabsTrigger value="vehicles" data-testid="view-vehicles">Vehicles</TabsTrigger>
                  <TabsTrigger value="drivers" data-testid="view-drivers">Drivers</TabsTrigger>
                  <TabsTrigger value="routes" data-testid="view-routes">Routes</TabsTrigger>
                  <TabsTrigger value="all" data-testid="view-all">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={defaultCenter}
              zoom={6}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                streetViewControl: false,
                mapTypeControl: true,
                fullscreenControl: true,
                styles: [
                  {
                    featureType: 'poi',
                    elementType: 'labels',
                    stylers: [{ visibility: 'off' }]
                  }
                ]
              }}
            >
              {/* Traffic Layer Overlay */}
              {showTraffic && <TrafficLayer />}
              
              {/* Depot Markers */}
              {depotMarkers.map((marker, idx) => (
                <Marker
                  key={`depot-${idx}`}
                  position={marker.position}
                  icon={markerIcons.depot}
                  onClick={() => setSelectedMarker(marker)}
                />
              ))}

              {/* Site Markers */}
              {siteMarkers.map((marker, idx) => (
                <Marker
                  key={`site-${idx}`}
                  position={marker.position}
                  icon={markerIcons.site}
                  onClick={() => setSelectedMarker(marker)}
                />
              ))}

              {/* Vehicle Markers - Truck Icons */}
              {vehicleMarkers.map((marker, idx) => (
                <Marker
                  key={`vehicle-${idx}`}
                  position={marker.position}
                  icon={createTruckIcon('#f97316')}
                  onClick={() => setSelectedMarker(marker)}
                />
              ))}

              {/* Driver Location Markers - Green Truck Icons for live tracking */}
              {driverMarkers.map((marker, idx) => (
                <Marker
                  key={`driver-${idx}`}
                  position={marker.position}
                  icon={createTruckIcon('#10b981')}
                  onClick={() => setSelectedMarker(marker)}
                />
              ))}

              {/* Directions Renderers - Routes following roads */}
              {directionsToDisplay.map((dirResult, idx) => (
                <DirectionsRenderer
                  key={`direction-${dirResult.routeId}`}
                  directions={dirResult.directions}
                  options={{
                    suppressMarkers: false,
                    preserveViewport: true,
                    polylineOptions: {
                      strokeColor: dirResult.color,
                      strokeWeight: selectedRoute === dirResult.routeId ? 6 : 4,
                      strokeOpacity: selectedRoute && selectedRoute !== dirResult.routeId ? 0.3 : 0.9
                    }
                  }}
                />
              ))}

              {/* Info Window */}
              {selectedMarker && (
                <InfoWindow
                  position={selectedMarker.position}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div className="p-2 min-w-[180px]">
                    <h3 className="font-bold text-gray-900 text-sm">{selectedMarker.name}</h3>
                    
                    {/* UK Registration Plate for vehicles/drivers */}
                    {selectedMarker.registration && (
                      <div className="my-2">
                        <div 
                          style={{
                            background: '#FFD500',
                            border: '2px solid #000',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#000',
                            letterSpacing: '1px',
                            display: 'inline-block'
                          }}
                        >
                          {selectedMarker.registration}
                        </div>
                      </div>
                    )}
                    
                    {/* Driver-specific info */}
                    {selectedMarker.type === 'driver' && (
                      <div className="space-y-1 my-2">
                        {selectedMarker.speed !== null && selectedMarker.speed !== undefined && (
                          <p className="text-xs text-gray-600">
                            Speed: {(selectedMarker.speed * 2.237).toFixed(0)} mph
                          </p>
                        )}
                        {selectedMarker.accuracy && (
                          <p className="text-xs text-gray-600">
                            Accuracy: ±{selectedMarker.accuracy.toFixed(0)}m
                          </p>
                        )}
                        {selectedMarker.updated_at && (
                          <p className="text-xs text-gray-400">
                            Updated: {new Date(selectedMarker.updated_at).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {selectedMarker.address && (
                      <p className="text-xs text-gray-600">{selectedMarker.address}</p>
                    )}
                    {selectedMarker.status && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                        {selectedMarker.status}
                      </span>
                    )}
                    <p className="text-xs text-gray-400 mt-1 capitalize">
                      {selectedMarker.type === 'driver' ? 'Live Location' : selectedMarker.type}
                    </p>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : (
            <div className="h-[550px] flex items-center justify-center bg-slate-100 rounded-lg">
              <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          )}
          
          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-navy-900 flex items-center justify-center">
                <Building2 className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm">Depots</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              <span className="text-sm">Vehicles</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-500" />
              <span className="text-sm">Drivers (Live)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500" />
              <span className="text-sm">Customer Sites</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-1 bg-orange-500 rounded" />
              <span className="text-sm">Routes (truck-friendly)</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm">Disruption</span>
            </div>
            {showTraffic && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground border-l pl-4">
                <span className="w-3 h-3 rounded-full bg-green-500" /> Fast
                <span className="w-3 h-3 rounded-full bg-yellow-500" /> Moderate
                <span className="w-3 h-3 rounded-full bg-red-500" /> Slow
              </div>
            )}
          </div>

          {/* Route List when routes view is active */}
          {(activeView === 'routes' || activeView === 'all') && routes.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">
                <Truck className="w-4 h-4 inline mr-1" />
                Routes optimized for large trucks with live traffic data. Click a route to highlight:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {routes.map((route, idx) => {
                  const routeInfo = directionsResults.find(d => d.routeId === route.load_id);
                  const hasDirections = !!routeInfo;
                  return (
                    <div 
                      key={route.load_id}
                      onClick={() => handleRouteClick(route.load_id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedRoute === route.load_id 
                          ? 'border-2 shadow-md' 
                          : 'hover:border-orange-500/50'
                      } ${!hasDirections && calculatingRoutes ? 'opacity-50' : ''}`}
                      style={{ 
                        borderLeftColor: routeColors[idx % routeColors.length], 
                        borderLeftWidth: '4px',
                        borderColor: selectedRoute === route.load_id ? routeColors[idx % routeColors.length] : undefined
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{route.route_name}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">{route.driver}</p>
                          <p className="text-xs text-muted-foreground">{route.stops?.length || 0} stops</p>
                          
                          {/* Route timing info */}
                          {routeInfo && (
                            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                              <div className="flex items-center gap-4 text-xs">
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Route className="w-3 h-3" />
                                  {formatDistance(routeInfo.totalDistance)}
                                </span>
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Clock className="w-3 h-3" />
                                  {formatDuration(routeInfo.totalDurationInTraffic || routeInfo.totalDuration)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-green-600">
                                  Final ETA: {routeInfo.legDetails[routeInfo.legDetails.length - 1]?.eta || 'N/A'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {/* Traffic Disruption Warning */}
                          {routeInfo?.hasTrafficDelay && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs font-medium" title={`Traffic adding ~${routeInfo.delayMinutes} min delay`}>
                              <AlertTriangle className="w-3 h-3" />
                              +{routeInfo.delayMinutes}m
                            </div>
                          )}
                          <div 
                            style={{
                              background: '#FFD500',
                              border: '1px solid #000',
                              borderRadius: '3px',
                              padding: '2px 6px',
                              fontFamily: 'monospace',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              color: '#000',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {route.vehicle}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {route.status}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Stop ETAs on selection */}
                      {selectedRoute === route.load_id && routeInfo && routeInfo.legDetails.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-200">
                          <p className="text-xs font-medium mb-2">Stop ETAs:</p>
                          <div className="space-y-1">
                            {routeInfo.legDetails.map((leg, legIdx) => (
                              <div key={legIdx} className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground truncate max-w-[60%]">
                                  {legIdx + 1}. {leg.address}
                                </span>
                                <span className="font-medium text-green-600">{leg.eta}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Panel */}
      {selectedMarker && selectedMarker.type === 'vehicle' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">{selectedMarker.name}</CardTitle>
                <CardDescription>Vehicle Details</CardDescription>
              </div>
              <div 
                style={{
                  background: '#FFD500',
                  border: '2px solid #000',
                  borderRadius: '4px',
                  padding: '6px 16px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#000',
                  letterSpacing: '2px'
                }}
              >
                {selectedMarker.registration}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Status</p>
                <Badge variant="outline">{selectedMarker.status}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Location</p>
                <p className="font-medium">
                  {selectedMarker.position.lat?.toFixed(4)}, {selectedMarker.position.lng?.toFixed(4)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Route Details Panel */}
      {selectedRoute && directionsResults.find(d => d.routeId === selectedRoute) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-orange-500" />
                  {directionsResults.find(d => d.routeId === selectedRoute)?.routeName}
                </CardTitle>
                <CardDescription>Route Details with Live Traffic</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedRoute(null)}>
                Clear Selection
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const routeInfo = directionsResults.find(d => d.routeId === selectedRoute);
              if (!routeInfo) return null;
              
              return (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Route className="w-3 h-3" /> Total Distance
                      </p>
                      <p className="text-lg font-bold">{formatDistance(routeInfo.totalDistance)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Est. Duration
                      </p>
                      <p className="text-lg font-bold">{formatDuration(routeInfo.totalDurationInTraffic || routeInfo.totalDuration)}</p>
                      {routeInfo.hasTrafficDelay && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Traffic delays
                        </p>
                      )}
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Stops</p>
                      <p className="text-lg font-bold">{routeInfo.routeData?.stops?.length || 0}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-700 font-medium">Final ETA</p>
                      <p className="text-lg font-bold text-green-700">
                        {routeInfo.legDetails[routeInfo.legDetails.length - 1]?.eta || 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Vehicle & Driver */}
                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div 
                      style={{
                        background: '#FFD500',
                        border: '2px solid #000',
                        borderRadius: '4px',
                        padding: '4px 12px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#000',
                        letterSpacing: '1px'
                      }}
                    >
                      {routeInfo.routeData?.vehicle}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{routeInfo.routeData?.driver}</p>
                      <p className="text-xs text-muted-foreground">Driver</p>
                    </div>
                    <Badge variant="outline" className="ml-auto">{routeInfo.routeData?.status}</Badge>
                  </div>
                  
                  {/* Detailed Stop List with ETAs */}
                  <div>
                    <h4 className="text-sm font-medium mb-2">Delivery Schedule:</h4>
                    <div className="space-y-2">
                      {/* Origin */}
                      <div className="flex items-center gap-3 p-2 bg-navy-50 rounded-lg border-l-4 border-navy-500">
                        <div className="w-6 h-6 rounded-full bg-navy-500 text-white flex items-center justify-center text-xs font-bold">
                          S
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">Start: {routeInfo.routeData?.stops?.[0]?.address}</p>
                          <p className="text-xs text-muted-foreground">Depot</p>
                        </div>
                        <span className="text-xs text-muted-foreground">Now</span>
                      </div>
                      
                      {/* Delivery Stops */}
                      {routeInfo.legDetails.map((leg, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200"
                          style={{ borderLeftColor: routeColors[routes.findIndex(r => r.load_id === selectedRoute) % routeColors.length], borderLeftWidth: '4px' }}
                        >
                          <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium truncate">{leg.address}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistance(leg.distance)} • {formatDuration(leg.duration)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-green-600">{leg.eta}</p>
                            <p className="text-xs text-muted-foreground">ETA</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Route Optimization Note */}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <Truck className="w-3 h-3 inline mr-1" />
                      Route optimized for large trucks: Using major roads, avoiding ferries, with live traffic data for accurate ETAs.
                    </p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MapsPage;
